import { useState, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Typography,
  Autocomplete,
  Checkbox
} from '@mui/material'
import { CheckBox, CheckBoxOutlineBlank } from '@mui/icons-material'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { userService } from '@shared/services/UserService'
import { Organization } from '@shared/types/entities'

interface InviteUserFormData {
  email: string
  firstName: string
  lastName: string
  role: 'Customer' | 'Agent' | 'Admin' | 'Oversight'
  message?: string
}

interface InviteUserDialogProps {
  open: boolean
  onClose: () => void
  onSuccess: () => void
  availableRoles: Array<'Customer' | 'Agent' | 'Admin' | 'Oversight'>
  childSites?: Organization[]
  organizationId?: string
  childSiteId?: string
}

const inviteUserSchema = yup.object({
  firstName: yup.string().required('First name is required').min(2, 'At least 2 characters'),
  lastName: yup.string().required('Last name is required').min(2, 'At least 2 characters'),
  email: yup.string().required('Email is required').email('Invalid email address'),
  role: yup.string().required('Role is required').oneOf(['Customer', 'Agent', 'Admin', 'Oversight'] as const),
  message: yup.string()
})

export function InviteUserDialog({
  open,
  onClose,
  onSuccess,
  availableRoles,
  childSites = [],
  organizationId,
  childSiteId
}: InviteUserDialogProps) {
  const [error, setError] = useState<string | null>(null)
  const [selectedRole, setSelectedRole] = useState<string>(availableRoles[0] || 'Customer')
  const [selectedOversightSites, setSelectedOversightSites] = useState<string[]>([])

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch
  } = useForm<InviteUserFormData>({
    resolver: yupResolver(inviteUserSchema),
    defaultValues: {
      role: availableRoles[0] || 'Customer'
    }
  })

  const currentRole = watch('role')

  useEffect(() => {
    setSelectedRole(currentRole)
  }, [currentRole])

  const handleClose = () => {
    reset()
    setError(null)
    setSelectedRole(availableRoles[0] || 'Customer')
    setSelectedOversightSites([])
    onClose()
  }

  const handleInvite = async (data: InviteUserFormData) => {
    try {
      setError(null)

      const request: any = {
        email: data.email,
        firstName: data.firstName,
        lastName: data.lastName,
        role: data.role,
        message: data.message
      }

      // Add organization/site context if provided
      if (organizationId) {
        request.organizationId = organizationId
      }
      if (childSiteId) {
        request.childSiteId = childSiteId
      }

      // Add oversight child sites if role is Oversight
      if (data.role === 'Oversight' && selectedOversightSites.length > 0) {
        request.oversightChildSiteIds = selectedOversightSites
      }

      await userService.inviteUser(request)

      handleClose()
      onSuccess()
    } catch (err: any) {
      setError(err.message || 'Failed to invite user')
      console.error('Failed to invite user:', err)
    }
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Invite {availableRoles.includes('Agent') || availableRoles.includes('Admin') ? 'Team Member' : 'User'}</DialogTitle>
      <form onSubmit={handleSubmit(handleInvite)}>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="First Name"
                placeholder="John"
                {...register('firstName')}
                error={!!errors.firstName}
                helperText={errors.firstName?.message}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Last Name"
                placeholder="Doe"
                {...register('lastName')}
                error={!!errors.lastName}
                helperText={errors.lastName?.message}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email Address"
                type="email"
                placeholder="john.doe@example.com"
                {...register('email')}
                error={!!errors.email}
                helperText={errors.email?.message}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth error={!!errors.role}>
                <InputLabel>Role</InputLabel>
                <Select
                  {...register('role')}
                  value={selectedRole}
                  onChange={(e) => {
                    setSelectedRole(e.target.value)
                    // Clear oversight sites when role changes
                    if (e.target.value !== 'Oversight') {
                      setSelectedOversightSites([])
                    }
                  }}
                  label="Role"
                >
                  {availableRoles.map((role) => (
                    <MenuItem key={role} value={role}>
                      {role}
                    </MenuItem>
                  ))}
                </Select>
                {errors.role && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                    {errors.role.message}
                  </Typography>
                )}
              </FormControl>
            </Grid>

            {/* Show child site multi-select only for Oversight role */}
            {selectedRole === 'Oversight' && childSites.length > 0 && (
              <Grid item xs={12}>
                <Autocomplete
                  multiple
                  options={childSites.filter(org => org.childSiteId)}
                  disableCloseOnSelect
                  getOptionLabel={(option) => option.name}
                  value={childSites.filter((org) => org.childSiteId && selectedOversightSites.includes(org.childSiteId))}
                  onChange={(_event, newValue) => {
                    setSelectedOversightSites(newValue.map((org) => org.childSiteId!).filter(Boolean))
                  }}
                  renderOption={(props, option, { selected }) => (
                    <li {...props}>
                      <Checkbox
                        icon={<CheckBoxOutlineBlank fontSize="small" />}
                        checkedIcon={<CheckBox fontSize="small" />}
                        style={{ marginRight: 8 }}
                        checked={selected}
                      />
                      {option.name}
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Oversight Child Sites"
                      placeholder="Select sites this user can oversee"
                      helperText="Select which child sites this oversight user can access"
                    />
                  )}
                />
              </Grid>
            )}

            {/* Optional message field for organization-level invites */}
            {organizationId && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Message (Optional)"
                  multiline
                  rows={3}
                  placeholder="Add a personal message to the invitation email..."
                  {...register('message')}
                  error={!!errors.message}
                  helperText={errors.message?.message}
                />
              </Grid>
            )}
          </Grid>

          <Alert severity="info" sx={{ mt: 2 }}>
            The user will receive an invitation email with a link to set their password
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button type="submit" variant="contained">
            Send Invitation
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}
